// Keep the fixed desktop reco sidebar in sync with the cart on AJAX updates.
// It is rendered as a sibling of <cart-drawer> (outside every section-replaced
// region) and only when the cart isn't empty, so the section-render flow never
// touches it. We reconcile its *presence* from a freshly rendered `cart-drawer`
// section: inject it when the cart becomes non-empty, drop it when it empties.
// While it stays present its cards are refreshed in place by the cart-reco-sidebar
// element's own `cart-update` listener, so we leave the live element untouched to
// avoid a redundant reco re-fetch / flash.
function refreshCartRecoSidebar(section) {
  if (!section) return;
  const doc =
    typeof section === 'string' ? new DOMParser().parseFromString(section, 'text/html') : section;
  const SELECTOR = 'cart-reco-sidebar:not(.cart-reco-sidebar--inline)';
  const source = doc.querySelector(SELECTOR);
  const target = document.querySelector(SELECTOR);

  if (source && !target) {
    // Empty -> non-empty: CSS reveals it via `:has(+ .drawer.active)`, so it must
    // land as the immediate previous sibling of <cart-drawer>.
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.parentNode.insertBefore(source, drawer);
  } else if (!source && target) {
    // Non-empty -> empty: drop the now-irrelevant sidebar.
    target.remove();
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    window.addEventListener('potions_add_to_cart', () => {
      this.onCartUpdate();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
        refreshCartRecoSidebar(html);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  renderContents(parsedState, onlySplitOrder = false) {
    document.querySelectorAll('.product-form__submit').forEach((btn) => (btn.disabled = true));
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (onlySplitOrder) {
        const sectionDOM = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector, true);
        const splitOrderContent = sectionDOM.querySelector('.cart-split-order');
        if (sectionElement.querySelector('.cart-split-order') && splitOrderContent) {
          sectionElement.querySelector('.cart-split-order').innerHTML = splitOrderContent.innerHTML;
        }
      } else {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }

      
      const total_price = document.querySelector('#alma-widget')?.dataset.price;
      const almaMerchantId = 'merchant_11lXVA85uF8n3ZQq8saWkEsw0kSus51xFA';

      const almaPlans = [
        {
          installmentsCount: 1,
          minAmount: 15000,
          maxAmount: 500000,
        },
        {
          installmentsCount: 3,
          minAmount: 15000,
          maxAmount: 500000,
        },
        {
          installmentsCount: 4,
          minAmount: 15000,
          maxAmount: 500000,
        },
      ];

      if (typeof Alma !== 'undefined' && Alma.Widgets) {
        const widgets = Alma.Widgets.initialize(almaMerchantId, Alma.ApiMode.LIVE);

        widgets.add(Alma.Widgets.PaymentPlans, {
          container: '#alma-widget',
          purchaseAmount: total_price,
          locale: 'fr',
          hideIfNotEligible: false,
          plans: almaPlans,
        });
        // Attendre que le widget soit visible dans le DOM avant de cacher le spinner
      }
    });

    refreshCartRecoSidebar(parsedState.sections['cart-drawer']);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (!onlySplitOrder) this.open();
      document.querySelectorAll('.product-form__submit').forEach((btn) => {
        btn.disabled = false;
        btn.removeAttribute('disabled');
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section', returnHTML = false) {
    if (returnHTML) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    } else {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
